import { useCallback } from "react"

import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { Button, Icon, Popup } from "semantic-ui-react"
import type { OpenAPI } from "simplydo/interfaces"

import actions from "actions"
import api from "api"
import { useAppDispatch, useAppSelector } from "store"
import util from "utils/utils"

type FollowButtonProps = {
  forType: string
  forId: string
  onFollow: () => void
  onUnfollow: () => void
  tooltip: string
}

const FollowButton = ({ forType, forId, onFollow, onUnfollow, tooltip }: FollowButtonProps) => {
  const user = useAppSelector((state) => state.user)
  const followObject = util.userIsFollowingContextObject(user, forType, forId)

  const dispatch = useAppDispatch()
  const updateUser = useCallback((user) => dispatch(actions.user.receiveUser(user)), [dispatch])
  const { t } = useTranslation()

  return (
    <Popup
      on="hover"
      trigger={
        <div>
          {followObject.following ? (
            <Button
              size="tiny"
              secondary
              onClick={() => {
                if (!onUnfollow) {
                  return
                }
                followObject.following = false
                followObject.unfollowed = true

                const oldFollowing = (user?.following || []).filter((f) => f._id !== forId)
                const newFollowing = [followObject, ...oldFollowing]

                updateUser({ ...user, following: newFollowing })
                onUnfollow()
              }}
            >
              <Icon name="star" />
              {t("users.following.title")}
            </Button>
          ) : (
            <Button
              size="tiny"
              basic
              inverted
              onClick={() => {
                if (!onFollow) {
                  return
                }
                followObject.following = true
                followObject.unfollowed = false

                const oldFollowing = (user?.following || []).filter((f) => f._id !== forId)
                const newFollowing = [followObject, ...oldFollowing]

                updateUser({ ...user, following: newFollowing })
                onFollow()
              }}
            >
              <Icon name="star outline" />
              {t("users.following.follow")}
            </Button>
          )}
        </div>
      }
      content={tooltip}
    />
  )
}

type ChallengeFollowButtonProps = {
  challenge: OpenAPI.Schemas["Challenge"]
}

export const ChallengeFollowButton = ({ challenge }: ChallengeFollowButtonProps) => {
  const { t } = useTranslation()
  const user = useAppSelector((state) => state.user)

  const followChallenge = useCallback(() => {
    api.challenges.follow(
      challenge._id,
      () => {
        toast.success(`${t("common:capitalise", { key: "generic.challenge" })} followed`)
      },
      (err) => {
        toast.error(err.message)
      },
    )
  }, [challenge, t])

  const unfollowChallenge = useCallback(() => {
    api.challenges.unfollow(
      challenge._id,
      () => {
        toast(`${t("common:capitalise", { key: "generic.challenge" })} unfollowed`)
      },
      (err) => {
        toast.error(err.message)
      },
    )
  }, [challenge, t])

  const isChallengeAdmin = util.hasPermission(user, "challenge.editSettings", challenge?._id)
  const isFollowing = util.userIsFollowingContext(user, "challenge", challenge?._id)
  const showUnfollowInfo = isFollowing && isChallengeAdmin

  if (!user) return null
  return (
    <FollowButton
      forType="challenge"
      forId={challenge._id}
      onFollow={!isChallengeAdmin && followChallenge}
      onUnfollow={!isChallengeAdmin && unfollowChallenge}
      tooltip={showUnfollowInfo ? t("users.following.cannotUnfollowInfo") : t("users.following.unfollowInfo")}
    />
  )
}

type IdeaFollowButtonProps = {
  idea: OpenAPI.Schemas["Idea"]
}

export const IdeaFollowButton = ({ idea }: IdeaFollowButtonProps) => {
  const { t } = useTranslation()
  const user = useAppSelector((state) => state.user)

  const followIdea = useCallback(() => {
    api.ideas.follow(
      idea._id,
      () => {
        toast.success(`${t("common:capitalise", { key: "generic.idea" })} followed`)
      },
      (err) => {
        toast.error(err.message)
      },
    )
  }, [idea, t])

  const unfollowIdea = useCallback(() => {
    api.ideas.unfollow(
      idea._id,
      () => {
        toast(`${t("common:capitalise", { key: "generic.idea" })} unfollowed`)
      },
      (err) => {
        toast.error(err.message)
      },
    )
  }, [idea, t])

  if (!user) return null

  return (
    <FollowButton
      forType="idea"
      forId={idea._id}
      onFollow={followIdea}
      onUnfollow={unfollowIdea}
      tooltip={`Follow ${t("generic.ideaWithArticle")} to receive notifications when somebody likes it or posts a comment. When creating or interacting with ${t("generic.ideaWithArticle")} you will automatically start following it.`}
    />
  )
}

type GroupFollowButtonProps = {
  group: OpenAPI.Schemas["Group"]
}

export const GroupFollowButton = ({ group }: GroupFollowButtonProps) => {
  const { t } = useTranslation()
  const user = useAppSelector((state) => state.user)

  const followGroup = useCallback(() => {
    api.groups.follow(
      group._id,
      () => {
        toast.success(`Group followed`)
      },
      (err) => {
        toast.error(err.message)
      },
    )
  }, [group])

  const unfollowGroup = useCallback(() => {
    api.groups.unfollow(
      group._id,
      () => {
        toast(`Group unfollowed`)
      },
      (err) => {
        toast.error(err.message)
      },
    )
  }, [group])

  if (!user) return null

  return (
    <FollowButton
      forType="group"
      forId={group._id}
      onFollow={followGroup}
      onUnfollow={unfollowGroup}
      tooltip={`Follow groups to receive notifications when there is activity in the group, such as new feed posts, discussion posts or ${t("generic.ideas")} submitted to related ${t("generic.challenges")}. When creating or interacting with groups you will automatically start following them.`}
    />
  )
}
